import { Dispatch, useContext, useEffect, useState } from "react";
import { BsCardChecklist, BsMagic, BsPeople, BsPersonAdd, BsSticky, BsTrash3, BsXSquare } from "react-icons/bs";
import { useSelector } from "react-redux";
import { IconMenuItems } from ".";
import { PriorityIcon } from "../../assets/svg/priority.icon";
import { ChangeEnquiryOwnershipContext } from "../../contexts/changeEnquiryOwnership.context";
import { ChangeEnquiryPriorityContext } from "../../contexts/changeEnquiryPriority.context";
import { CreateNoteContext } from "../../contexts/create-note.context";
import { EnquiryGridSelectionContext } from "../../contexts/enquiry-grid-selection.context";
import { EnquiryPropertiesContext } from "../../contexts/enquiry-properties.context";
import { RemoveEnquiriesContext } from "../../contexts/removeEnquiries.context";
import { ReplaceEnquiryOwnershipContext } from "../../contexts/replaceEnquiryOwnership.context";
import { NoActionRequiredEnquiriesContext } from "../../contexts/noActionRequiredEnquiries.context";
import { EnquiryMenuProps } from "../../interfaces/menu";
import { GetMailboxPermissions } from "../../interfaces/Permissions";
import Statuses from "../../interfaces/Statuses";
import { GetEnquiryCommunications, TakeOwnership } from "../../Logic/EmsAPI/Enquiries";
import { ActivateWizardModule } from "../../Logic/Enquiries/activateWizard";
import { ReloadActiveList } from "../../Logic/Enquiries/Load";
import { RootState, useAppDispatch } from "../../state/store";

interface EnquiryMenuPropswithHide extends EnquiryMenuProps {
    onHide: () => void;
}
export function EnquiryMenuItems({ enquiryIds, menuStyle, onHide }: EnquiryMenuPropswithHide) {
    const createNote = useContext(CreateNoteContext);
    const changeEnquiryOwnership = useContext(ChangeEnquiryOwnershipContext);
    const removeEnquiries = useContext(RemoveEnquiriesContext);
    const noActionRequiredEnquiries = useContext(NoActionRequiredEnquiriesContext);
    const changeEnquiryPriority = useContext(ChangeEnquiryPriorityContext);
    const replaceEnquiryOwnership = useContext(ReplaceEnquiryOwnershipContext);
    const enquiryProperties = useContext(EnquiryPropertiesContext);
    const permissions = useSelector((state: RootState) => state.permissions);
    const User = useSelector((state: RootState) => state.UserInformation);
    const dispatch: Dispatch<any> = useAppDispatch();
    const { selectionProperties, SetEnquiryGridSelectionProperties } = useContext(EnquiryGridSelectionContext);
    const enquiriesCache = useSelector((state: RootState) => state.EnquiryCache.data);
    const enquiries = enquiriesCache.filter(x => enquiryIds.includes(x.id));
    const [LastCommFromEmail, setlastCommEmail] = useState("");

    useEffect(() => {
        const checkEmailMatch = async () => {
            const lastEmail = await GetLastComm(enquiries[0].id);
            setlastCommEmail(lastEmail);
        };
        checkEmailMatch();
    }, []);

    function canTakeOwnership(): boolean {
        if (!enquiries || enquiries.length == 0) return false;

        if (enquiries.find(o => o.owners?.find(o => o.id === User.userID))) return false;

        for (const enquiry of enquiries) {
            const mailboxPermissions = GetMailboxPermissions(permissions, enquiry.mailbox);

            const hasReplace = mailboxPermissions.ReplaceEnquiryOwnership;
            const hasTake = mailboxPermissions.TakeEnquiryOwnership;
            const hasOwners = enquiry.owners.length > 0;

            if (hasOwners && !hasReplace) return false;

            if (!hasTake && !hasReplace) return false;
        }

        return true;
    }

    function EnquiriesHasOwners(): boolean {
        return enquiries.find(x => x.owners.length > 0) ? true : false;
    }

    function EnquiriesInProcessing(): boolean {
        return enquiries.find(x => x.status === Statuses.Processing.ID) ? true : false;
    }

    async function GetLastComm(enquiryId: number): Promise<string> {
        const comm = await GetEnquiryCommunications(enquiryId);
        const lastComm = comm.data
            ?.slice()
            .sort((a, b) => new Date(a.dateStamp).getTime() - new Date(b.dateStamp).getTime())
            .at(-1);

        return lastComm?.email?.from.address ?? "";
    }
    function HasActivateWizardPermission(): boolean {
        return GetMailboxPermissions(permissions, enquiries[0].mailbox).ActionUnownedEnquiry === true &&
            enquiries[0].owners.length === 0
            ? GetMailboxPermissions(permissions, enquiries[0].mailbox).ActionUnownedEnquiry
            : GetMailboxPermissions(permissions, enquiries[0].mailbox).ActionOtherOwnedEnquiry === true &&
                enquiries[0].owners.length >= 0
              ? GetMailboxPermissions(permissions, enquiries[0].mailbox).ActionOtherOwnedEnquiry
              : new ActivateWizardModule().wizardPermitted(enquiries[0]);
    }
    return enquiries != undefined && enquiries.length == 0 ? (
        <></>
    ) : (
        <IconMenuItems
            options={[
                {
                    icon: (
                        <i>
                            <BsMagic />
                        </i>
                    ),
                    label: "Activate Wizard",
                    onClick: () => {
                        new ActivateWizardModule().ActivateWizard(
                            enquiryIds[0],
                            undefined,
                            HasActivateWizardPermission(),
                        );
                    },
                    disabled: !HasActivateWizardPermission() || selectionProperties.multiSelect,
                },

                {
                    icon: (
                        <i>
                            <BsXSquare />
                        </i>
                    ),
                    label: "No Action Required",
                    onClick: () => {
                        noActionRequiredEnquiries(enquiries);
                        onHide();
                    },
                    disabled:
                        !enquiries ||
                        selectionProperties.multiSelect ||
                        !enquiries[0].hasNew ||
                        !(enquiries[0].status !== Statuses.Received.ID) ||
                        !(enquiries[0].status !== Statuses.Processing.ID) ||
                        (enquiries[0].enquirerEmail.toLocaleLowerCase() === LastCommFromEmail.toLocaleLowerCase() &&
                            (enquiries[0].status === Statuses.PendCorr.ID ||
                                enquiries[0].status === Statuses.Pending.ID ||
                                enquiries[0].status === Statuses.PendRif.ID)),
                },
                {
                    icon: (
                        <i>
                            <BsSticky />
                        </i>
                    ),
                    label: "Create Note",
                    onClick: () => {
                        createNote(enquiries[0].id);
                        onHide();
                    },
                    disabled: !enquiries || enquiries.length !== 1 || selectionProperties.multiSelect,
                },
                {
                    icon: (
                        <i>
                            <BsPersonAdd />
                        </i>
                    ),
                    label: "Take Ownership",
                    onClick: async () => {
                        if (enquiries.find(op => op.owners?.length === 0)) {
                            await TakeOwnership(enquiryIds);

                            if (
                                selectionProperties.multiSelect &&
                                User.preferences.general.enquiryList.multiSelectMode == "multi-preview"
                            ) {
                                SetEnquiryGridSelectionProperties({ multiSelect: false, selectedEnquiries: [] });
                            }

                            await ReloadActiveList();
                        } else {
                            replaceEnquiryOwnership(enquiries);
                        }

                        onHide();
                    },
                    disabled: !canTakeOwnership() || EnquiriesInProcessing(),
                },
                {
                    icon: (
                        <i>
                            <BsPeople />
                        </i>
                    ),
                    label: "Change Enquiry Ownership",
                    onClick: () => {
                        changeEnquiryOwnership(enquiries);
                        onHide();
                    },
                    disabled: enquiries.find(
                        x =>
                            !GetMailboxPermissions(permissions, x.mailbox).ModifyEnquiryOwnership ||
                            x.status == Statuses.Processing.ID,
                    )
                        ? true
                        : false,
                },
                {
                    icon: (
                        <i>
                            <PriorityIcon
                                color={
                                    enquiries.find(
                                        x =>
                                            !GetMailboxPermissions(permissions, x.mailbox).ModifyEnquiryPriority ||
                                            x.status !== Statuses.Received.ID,
                                    )
                                        ? "#E6E7E8"
                                        : "black"
                                }
                            />
                        </i>
                    ),
                    label: "Change Enquiry Priority",
                    onClick: () => {
                        changeEnquiryPriority(enquiries);
                        onHide();
                    },
                    disabled: enquiries.find(
                        x =>
                            !GetMailboxPermissions(permissions, x.mailbox).ModifyEnquiryPriority ||
                            x.status !== Statuses.Received.ID,
                    )
                        ? true
                        : false,
                },
                {
                    icon: (
                        <i>
                            <BsTrash3 />
                        </i>
                    ),
                    label: "Remove Enquiry",
                    onClick: () => {
                        removeEnquiries(enquiries);
                        onHide();
                    },
                    hidden: enquiries.find(x => !GetMailboxPermissions(permissions, x.mailbox).CloseEnquiry)
                        ? true
                        : false,
                    disabled:
                        enquiries.find(x => x.status != Statuses.Received.ID) ||
                        enquiries.find(
                            x => x.owners.length > 0 && !(x.owners.length === 1 && x.owners[0].id === User.userID),
                        )
                            ? true
                            : false,
                },
                {
                    icon: (
                        <i>
                            <BsCardChecklist />
                        </i>
                    ),
                    label: "Enquiry Properties",
                    onClick: () => {
                        enquiryProperties(enquiries[0].id);
                        onHide();
                    },
                    disabled: !enquiries || enquiries.length !== 1 || selectionProperties.multiSelect,
                },
            ]}
            menuStyle={menuStyle ?? "context-menu"}
        />
    );
}
